import type { ReceiptContent } from "@shared/receipt";

/**
 * Pure reducer + helpers for the inline ReceiptEditor. Extracted from the
 * component so the optimistic-update + dirty-bit logic can be unit-tested
 * without rendering the DOM.
 *
 * Design: state holds the in-progress local ReceiptContent plus a `dirtyAt`
 * timestamp set every time the user edits. The hook layer reads `dirtyAt` to
 * trigger a debounced persist and then calls `markPersisted` to clear it.
 */

export type ListField = "highlights" | "shipped" | "thanks";

/**
 * Any section a line can be moved between. `blockers` is included even
 * though its rows are `{ text, owner }` objects (not plain strings) —
 * the `moveItem` reducer normalises across both shapes so the user can
 * re-file a line from ANY section to ANY other.
 */
export type MovableField = ListField | "blockers";

export type EditorState = {
  content: ReceiptContent;
  /** Last edit timestamp (ms since epoch). 0 means clean / persisted. */
  dirtyAt: number;
};

export type EditorAction =
  | { type: "setHeadline"; value: string }
  | { type: "setListItem"; field: ListField; index: number; value: string }
  | { type: "addListItem"; field: ListField; value?: string }
  | { type: "removeListItem"; field: ListField; index: number }
  | {
      /** Re-file a line from ANY section to ANY other (highlights ⇄
       *  shipped ⇄ blockers …). Source/target shapes are normalised by
       *  the reducer so the user never has to retype. */
      type: "moveItem";
      from: MovableField;
      index: number;
      to: MovableField;
    }
  | { type: "setBlockerText"; index: number; value: string }
  | { type: "setBlockerOwner"; index: number; value: string }
  | { type: "addBlocker"; text?: string }
  | { type: "removeBlocker"; index: number }
  | { type: "reset"; content: ReceiptContent }
  | { type: "markPersisted" };

export function initialState(content: ReceiptContent): EditorState {
  return { content, dirtyAt: 0 };
}

export function editorReducer(state: EditorState, action: EditorAction): EditorState {
  switch (action.type) {
    case "setHeadline": {
      if (state.content.headline === action.value) return state;
      return {
        content: { ...state.content, headline: action.value },
        dirtyAt: now(),
      };
    }
    case "setListItem": {
      const list = state.content[action.field];
      if (list[action.index] === action.value) return state;
      const next = list.slice();
      next[action.index] = action.value;
      return {
        content: { ...state.content, [action.field]: next },
        dirtyAt: now(),
      };
    }
    case "addListItem": {
      const next = [...state.content[action.field], action.value ?? ""];
      return {
        content: { ...state.content, [action.field]: next },
        dirtyAt: now(),
      };
    }
    case "removeListItem": {
      const next = state.content[action.field].filter((_, i) => i !== action.index);
      return {
        content: { ...state.content, [action.field]: next },
        dirtyAt: now(),
      };
    }
    case "moveItem": {
      if (action.from === action.to) return state;

      // 1. Read the moved line's text, normalising across the two
      //    shapes: blockers are `{ text, owner }`, every other section
      //    is a plain string.
      const text: string | null =
        action.from === "blockers"
          ? (state.content.blockers[action.index]?.text ?? null)
          : (state.content[action.from][action.index] ?? null);
      if (text == null) return state;

      // 2. Remove from the source section.
      const content: ReceiptContent = { ...state.content };
      if (action.from === "blockers") {
        content.blockers = state.content.blockers.filter(
          (_, i) => i !== action.index,
        );
      } else {
        content[action.from] = state.content[action.from].filter(
          (_, i) => i !== action.index,
        );
      }

      // 3. Append to the target section, re-wrapping into the blocker
      //    object shape when that's the destination. Moving INTO
      //    blockers drops the owner (a highlight has no owner); moving
      //    OUT of blockers keeps only the text.
      if (action.to === "blockers") {
        content.blockers = [...content.blockers, { text }];
      } else {
        content[action.to] = [...content[action.to], text];
      }

      return { content, dirtyAt: now() };
    }
    case "setBlockerText": {
      const cur = state.content.blockers[action.index];
      if (!cur || cur.text === action.value) return state;
      const next = state.content.blockers.slice();
      next[action.index] = { ...cur, text: action.value };
      return { content: { ...state.content, blockers: next }, dirtyAt: now() };
    }
    case "setBlockerOwner": {
      const cur = state.content.blockers[action.index];
      if (!cur) return state;
      const owner = action.value.trim() === "" ? undefined : action.value;
      if (cur.owner === owner) return state;
      const next = state.content.blockers.slice();
      next[action.index] = { ...cur, owner };
      return { content: { ...state.content, blockers: next }, dirtyAt: now() };
    }
    case "addBlocker": {
      const next = [...state.content.blockers, { text: action.text ?? "" }];
      return { content: { ...state.content, blockers: next }, dirtyAt: now() };
    }
    case "removeBlocker": {
      const next = state.content.blockers.filter((_, i) => i !== action.index);
      return { content: { ...state.content, blockers: next }, dirtyAt: now() };
    }
    case "reset": {
      return { content: action.content, dirtyAt: 0 };
    }
    case "markPersisted": {
      if (state.dirtyAt === 0) return state;
      return { ...state, dirtyAt: 0 };
    }
  }
}

function now(): number {
  return typeof Date !== "undefined" ? Date.now() : 0;
}

/**
 * True iff a thrown persist error is an optimistic-concurrency / stale-row
 * conflict (vs. a network error, validation error, etc.).
 *
 * tRPC surfaces the code in different shapes depending on link/version
 * (`err.data.code` for the httpBatchLink error formatter, `err.code` for a
 * raw TRPCError), and the server also tags the message `stale_receipt`.
 * Centralising this classification keeps the editor's conflict-recovery
 * (rebase-and-retry) from silently breaking if any one of those shapes
 * changes — the regression that made an edit-after-approve disappear.
 *
 * Pure helper — no React, fully unit-testable.
 */
export function isStaleConflictError(err: unknown): boolean {
  if (!err || typeof err !== "object") return false;
  const e = err as {
    code?: unknown;
    data?: { code?: unknown } | null;
    message?: unknown;
  };
  const code = e.data?.code ?? e.code;
  if (code === "CONFLICT") return true;
  return String(e.message ?? "").includes("stale_receipt");
}

/**
 * Decide whether a debounced persist should fire. Returns the action ("save"
 * or "wait" or "idle") plus the milliseconds to wait before re-checking.
 *
 * Pure helper — no timers, no React. Lets us test the debounce logic without
 * running fake timers in a DOM environment.
 */
export function shouldPersist(
  state: EditorState,
  nowMs: number,
  debounceMs = 300,
): { action: "save" | "wait" | "idle"; waitMs: number } {
  if (state.dirtyAt === 0) return { action: "idle", waitMs: 0 };
  const elapsed = nowMs - state.dirtyAt;
  if (elapsed < 0) return { action: "wait", waitMs: debounceMs };
  if (elapsed >= debounceMs) return { action: "save", waitMs: 0 };
  return { action: "wait", waitMs: debounceMs - elapsed };
}
